/**
 * The ONE default-value picker.
 *
 * A `default_value` in this platform is a small vocabulary — auto-generate
 * functions, audit stamps, calendar values and project variables — and the
 * transaction designer had the only editor for it. The document repository's
 * metadata fields needed the same vocabulary, and the wrong answer would have
 * been a second, smaller menu that drifts the first time a token is added.
 * So the picker, its validator, and the token predicates live here and BOTH
 * designers import them.
 *
 * Selecting an entry writes its `:<token>` / `fn()` form into the caller's
 * default value; what the caller does with `protect` afterwards is its own
 * rule (a transaction column force-protects on an audit stamp; a document
 * metadata field has an explicit "always the default" switch).
 */

import { useState, useEffect, useMemo, useRef } from 'react'
import { ChevronDown, Search } from 'lucide-react'
import Modal from '@/components/common/Modal'
import {
  AUDIT_TOKENS, AUDIT_TOKEN_LABELS,
  CALENDAR_TOKENS, CALENDAR_TOKEN_LABELS,
  projectVarToken,
} from '@/livepreview/utils'
import type { ProjectVariable } from '@/api/projectVariables'

/**
 * Validate a default value. A leading `:` declares this as a
 * resolve-at-save token — it must therefore match a known audit token
 * or an existing project variable, otherwise the runtime has nothing
 * to stamp and the field will end up blank. Returns an error string
 * for invalid token references, or null when the value is acceptable
 * (either a recognised token or a literal default).
 */
export function validateTokenDefault(
  value: string | undefined,
  projectVars: ProjectVariable[],
): string | null {
  const raw = (value || '').trim()
  if (!raw.startsWith(':')) return null
  const name = raw.slice(1).trim().toLowerCase()
  if (!name) return 'Token name missing after “:”.'
  if ((AUDIT_TOKENS as readonly string[]).includes(`:${name}`)) return null
  // Calendar tokens accept an optional +N/-N day-offset suffix
  // (":calendar_today+7") — validate against the base token.
  const calBase = name.replace(/[+-]\d{1,4}$/, '')
  if ((CALENDAR_TOKENS as readonly string[]).includes(`:${calBase}`)) return null
  if (projectVars.some((v) => v.name === name)) return null
  return `“:${name}” is neither an audit/calendar token nor a project ` +
    `variable. Pick one from the “:” menu or define it in Workbench → ` +
    `Variables.`
}

// Server-resolved auto-generate functions offered in the default-value
// picker. `auto_generate()` is a table-wide serial (header ids); the line-no
// variant numbers a detail row WITHIN its parent (restarts per parent); the
// prefixed variant counts per prefix, where the prefix is composed from the
// row's own data; the device variant is the offline-safe per-device series.
export const AUTO_GEN_FUNCTIONS: { token: string; label: string }[] = [
  { token: 'auto_generate()', label: 'Unique serial across the whole table (e.g. a header id/number)' },
  { token: 'auto_generate_line_no()', label: 'Line number within the parent — restarts per parent (detail / sub-detail key)' },
  { token: 'auto_generate_prefixed()', label: 'Serial with a prefix built from this form’s data — a separate counter per prefix (IND0000001, USA0000001)' },
  { token: 'auto_generate_device()', label: 'Per-device series for offline capture (INV-D03-0001)' },
]

// True for any auto-generate token (whitespace-tolerant), so picking one
// leaves the column editable instead of force-protecting it.
export function isAutoGenToken(token: string): boolean {
  const raw = (token || '').trim().toLowerCase().replace(/\s+/g, '')
  return raw === 'auto_generate()' || raw === 'auto_generate_line_no()'
    || raw === 'auto_generate_prefixed()' || raw === 'auto_generate_device()'
}

// The prefixed variant is the one token that needs a companion `series` block,
// so the panel shows its builder — and opens it straight after the pick, since
// the token alone generates nothing.
export function isPrefixedSeriesToken(token: string): boolean {
  return (token || '').trim().toLowerCase().replace(/\s+/g, '')
    === 'auto_generate_prefixed()'
}

export interface PickerEntry {
  token: string
  label: string
}

export interface PickerGroup {
  title: string
  entries: readonly PickerEntry[]
}

export default function DefaultValuePicker({
  projectVars, current, onPick, hideAudit = false, hideCalendar = false,
  autoGenTokens, extraGroups,
}: {
  projectVars: ProjectVariable[]
  current: string
  onPick: (token: string) => void
  // Hide the audit-stamp group — irrelevant for a date-range default bound.
  hideAudit?: boolean
  // Hide the calendar values. Set by a surface whose target is not a date:
  // a `:calendar_month_start` resolves to a date, and a date compared against
  // a text or numeric column is an error the database raises at run time, so
  // the token must not be OFFERED where it cannot land.
  hideCalendar?: boolean
  // Which auto-generate functions this surface can honour. Omitted ⇒ all of
  // them, which is the transaction designer. A document repository has no
  // parent row and no device series, so it passes the two it can actually
  // fill — offering a token the surface will never resolve is worse than
  // not offering it.
  autoGenTokens?: readonly string[]
  // Surface-specific token groups (the repository's user-information tokens),
  // rendered above the calendar values and searched with everything else.
  extraGroups?: readonly PickerGroup[]
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const searchRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!open) { setQuery(''); return }
    // Focus the search field shortly after the modal renders.
    const t = window.setTimeout(() => searchRef.current?.focus(), 30)
    return () => window.clearTimeout(t)
  }, [open])

  type Entry = {
    token: string
    label: string
    group: 'function' | 'audit' | 'calendar' | 'variable' | string
  }
  const entries: Entry[] = useMemo(() => {
    // Auto-generate functions are irrelevant for a date-range filter bound
    // (the hideAudit contexts), so gate them on the same flag.
    const offered = autoGenTokens
      ? AUTO_GEN_FUNCTIONS.filter((f) => autoGenTokens.includes(f.token))
      : AUTO_GEN_FUNCTIONS
    const funcs: Entry[] = hideAudit ? [] : offered.map((f) => ({
      token: f.token, label: f.label, group: 'function',
    }))
    const audit: Entry[] = hideAudit ? [] : AUDIT_TOKENS.map((t) => ({
      token: t, label: AUDIT_TOKEN_LABELS[t], group: 'audit',
    }))
    const cal: Entry[] = hideCalendar ? [] : CALENDAR_TOKENS.map((t) => ({
      token: t, label: CALENDAR_TOKEN_LABELS[t], group: 'calendar',
    }))
    const vars: Entry[] = projectVars.map((v) => ({
      token: projectVarToken(v.name),
      label: v.description || (v.kind === 'sql' ? 'SQL query' : 'Fixed value'),
      group: 'variable',
    }))
    const extra: Entry[] = (extraGroups || []).flatMap((g) => g.entries.map(
      (e) => ({ token: e.token, label: e.label, group: `x:${g.title}` })))
    return [...funcs, ...audit, ...extra, ...cal, ...vars]
  }, [projectVars, hideAudit, hideCalendar, autoGenTokens, extraGroups])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return entries
    return entries.filter(
      (e) => e.token.toLowerCase().includes(q) || e.label.toLowerCase().includes(q),
    )
  }, [entries, query])

  const funcs = filtered.filter((e) => e.group === 'function')
  const audit = filtered.filter((e) => e.group === 'audit')
  const cal = filtered.filter((e) => e.group === 'calendar')
  const vars = filtered.filter((e) => e.group === 'variable')

  const pick = (tok: string) => {
    onPick(tok)
    setOpen(false)
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={hideCalendar
          ? 'Insert a project variable'
          : 'Insert an auto-generate function, audit stamp, calendar value, or project variable'}
        aria-haspopup="dialog"
        style={{
          padding: '6px 8px',
          border: '1px solid var(--color-border)',
          borderRadius: 4,
          background: 'var(--color-bg-secondary)',
          cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', gap: 4,
          color: 'var(--color-text)',
        }}
      >
        <span style={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>:</span>
        <ChevronDown size={12} />
      </button>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Insert default value token"
        size="default"
      >
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '8px 10px', marginBottom: 10,
          border: '1px solid var(--color-border)',
          borderRadius: 6,
          background: 'var(--lp-input-bg)',
        }}>
          <Search size={14} style={{ color: 'var(--color-text-secondary)' }} />
          <input
            ref={searchRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search functions, audit stamps, and project variables…"
            style={{
              flex: 1, border: 'none', background: 'transparent',
              outline: 'none', color: 'var(--color-text)',
              fontSize: '0.9625rem',
            }}
          />
        </div>
        <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
          {!hideAudit && (
            <PickerSection title="Auto-generate">
              {funcs.length === 0 ? (
                <EmptyHint>No match.</EmptyHint>
              ) : funcs.map((e) => (
                <PickerItem
                  key={e.token}
                  token={e.token}
                  label={e.label}
                  active={current === e.token}
                  onClick={() => pick(e.token)}
                />
              ))}
            </PickerSection>
          )}
          {!hideAudit && (
            <PickerSection title="Audit stamps">
              {audit.length === 0 ? (
                <EmptyHint>No match.</EmptyHint>
              ) : audit.map((e) => (
                <PickerItem
                  key={e.token}
                  token={e.token}
                  label={e.label}
                  active={current === e.token}
                  onClick={() => pick(e.token)}
                />
              ))}
            </PickerSection>
          )}
          {(extraGroups || []).map((g) => (
            <PickerSection key={g.title} title={g.title}>
              {filtered.filter((e) => e.group === `x:${g.title}`).length === 0 ? (
                <EmptyHint>No match.</EmptyHint>
              ) : filtered.filter((e) => e.group === `x:${g.title}`).map((e) => (
                <PickerItem
                  key={e.token}
                  token={e.token}
                  label={e.label}
                  active={current === e.token}
                  onClick={() => pick(e.token)}
                />
              ))}
            </PickerSection>
          ))}
          {!hideCalendar && (
          <PickerSection title="Calendar values">
            {cal.length === 0 ? (
              <EmptyHint>No match.</EmptyHint>
            ) : cal.map((e) => (
              <PickerItem
                key={e.token}
                token={e.token}
                label={e.label}
                active={current === e.token}
                onClick={() => pick(e.token)}
              />
            ))}
          </PickerSection>
          )}
          <PickerSection title="Project variables">
            {projectVars.length === 0 ? (
              <EmptyHint>
                None defined. Add some in Workbench → Variables.
              </EmptyHint>
            ) : vars.length === 0 ? (
              <EmptyHint>No match.</EmptyHint>
            ) : vars.map((e) => (
              <PickerItem
                key={e.token}
                token={e.token}
                label={e.label}
                active={current === e.token}
                onClick={() => pick(e.token)}
              />
            ))}
          </PickerSection>
        </div>
        {current && (
          <div style={{
            marginTop: 12, paddingTop: 10,
            borderTop: '1px solid var(--color-border)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span style={{
              fontSize: '0.875rem', color: 'var(--color-text-secondary)',
            }}>
              Current: <code style={{ color: 'var(--color-text)' }}>{current}</code>
            </span>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => pick('')}
            >
              Clear
            </button>
          </div>
        )}
      </Modal>
    </>
  )
}

function PickerSection({ title, children }: {
  title: string
  children: React.ReactNode
}) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{
        padding: '4px 8px', fontSize: '0.8125rem',
        color: 'var(--color-text-secondary)',
        textTransform: 'uppercase', letterSpacing: '0.04em',
        fontWeight: 700,
      }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {children}
      </div>
    </div>
  )
}

function EmptyHint({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      padding: '8px 10px', fontSize: '0.875rem',
      color: 'var(--color-text-secondary)', fontStyle: 'italic',
    }}>{children}</div>
  )
}

function PickerItem({
  token, label, active, onClick,
}: {
  token: string
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'baseline', gap: 8,
        width: '100%', padding: '6px 10px',
        textAlign: 'left', cursor: 'pointer',
        background: active ? 'var(--color-accent-weak)' : 'transparent',
        color: active ? 'var(--color-accent)' : 'var(--color-text)',
        border: 'none', borderRadius: 4,
        fontSize: '0.9125rem', lineHeight: 1.25,
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.background = 'var(--color-bg-secondary)'
        }
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = 'transparent'
      }}
    >
      <span style={{
        fontFamily: 'monospace', fontWeight: 600,
        flexShrink: 0,
      }}>{token}</span>
      <span style={{
        flex: 1, fontSize: '0.8125rem',
        color: 'var(--color-text-secondary)',
        overflow: 'hidden', textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>{label}</span>
    </button>
  )
}
